import {
  BoxGeometry,
  Euler,
  Mesh,
  Object3D,
  PlaneGeometry,
  Vector3,
} from "three";
import { createBoxOptions } from "../../../util/GeometryUtil";
import { nextWall, prevWall } from "../../../util/SceneUtil";
import { Line } from "../../Line";
import { Meter } from "../../Meter";
import { Point } from "../../Point";
import { Wall } from "../Wall";
import { GeometryNodeExecutor } from "./GeometryNodeExecutor";
import {
  baseMaterial,
  glassMaterial,
  glassSideMaterial,
  glassInterMaterial,
  kavcukMaterial,
} from "./Materials";

export class Sb24Executor implements GeometryNodeExecutor {
  private _glasses: Object3D[] = [];
  private _base?: Object3D;
  private _line?: Line;
  protected fixation = 0;
  private _isMeterVisible = true;
  private _executed = false;

  protected glassLowerProfile = 0.027;
  private glassDepth = 0.008;
  private glassHeight = 1.01;

  private baseHeight = 0.12;
  private baseDepth = 0.05;

  private get paddingIn() {
    return this.fixation
      ? -this.baseDepth
      : this.getAttribute("Offset") >= 0
      ? this.getAttribute("Offset") / 1000
      : this.baseDepth;
  }

  constructor() {}

  get isExecuted(): boolean {
    return this._executed;
  }

  set line(value: Line) {
    this._line = value;
  }

  get line() {
    return this._line!;
  }

  get parameters(): any {
    return {
      width: this._line?.length,
      height: this.glassHeight,
    };
  }

  setAttribute(name: string, value: any) {
    (this as any)[name] = value;
  }

  getAttribute(name: string) {
    return (this as any)[name];
  }

  private _getSizeInfo() {
    let basePaddingStart = 0;
    let basePaddingEnd = 0;
    let glassPaddingStart = 0.0;
    let glassPaddingEnd = 0.0;

    let profileDepth = this.baseDepth;
    let basePadding = this.paddingIn;

    let next = nextWall(this._line);
    if ((next as any)?._type === "Balcony" && next?.direction) {
      const cross = this._line?.direction?.clone().cross(next.direction!);
      basePaddingEnd = basePadding;
      if (cross && cross.y > 0) {
        glassPaddingEnd = -basePadding;
        basePaddingEnd = -(basePadding + profileDepth);
      } else {
        glassPaddingEnd = basePadding + profileDepth;
      }
      if (this.fixation) {
        glassPaddingEnd = 0.0;
        basePaddingEnd = -profileDepth;
      }
    } else if (next instanceof Wall) {
      glassPaddingEnd = 0.02;
      basePaddingEnd = 0;
    }

    let prev = prevWall(this._line);
    if ((prev as any)?._type === "Balcony" && prev?.direction) {
      const cross = this._line?.direction?.clone().cross(prev.direction!);
      basePaddingStart = basePadding;
      if (cross && cross.y > 0) {
        glassPaddingStart = basePadding + profileDepth;
      } else {
        glassPaddingStart = -basePadding;
        basePaddingStart = -(basePadding + profileDepth);
      }
      if (this.fixation) {
        glassPaddingStart = 0;
        basePaddingStart = -profileDepth;
      }
    } else if (prev instanceof Wall) {
      glassPaddingStart = 0.02;
      basePaddingStart = 0;
    }

    const totalLenght = this._line!.length - basePaddingStart - basePaddingEnd;

    const glassTotalLength =
      this._line!.length - (glassPaddingEnd + glassPaddingStart);
    const glassCount = Math.floor(glassTotalLength / 1.5) + 1;
    const glassLength =
      Math.floor(
        (1000 * (glassTotalLength - (glassCount - 1) * 0.01)) / glassCount
      ) / 1000;

    return {
      glassPaddingStart: glassPaddingStart,
      glassPaddingEnd: glassPaddingEnd,
      glassTotalLength: glassTotalLength,
      glassWidth: glassLength,
      glassHeight: this.glassHeight,
      glassDepth: this.glassDepth,
      glassLowerProfile: this.glassLowerProfile,
      glassCount: glassCount,
      baseWidth: totalLenght,
      baseHeight: this.baseHeight,
      baseDepth: this.baseDepth,
      paddingIn: this.paddingIn,
      basePaddingEnd: basePaddingEnd,
      basePaddingStart: basePaddingStart,
      hasGlass: true,
    };
  }

  private _createGlass(sizeInfo: any, index: number) {
    const glass = new Object3D();

    glass.position.set(
      sizeInfo.glassPaddingStart +
        index * (sizeInfo.glassWidth + 0.01) +
        sizeInfo.glassWidth * 0.5,
      sizeInfo.glassHeight * 0.5,
      sizeInfo.baseDepth * 0.5 + sizeInfo.paddingIn
    );

    const glass1 = new Object3D();

    glass1.position.setY(0);
    glass1.position.setZ(sizeInfo.glassDepth * 0.5 + 0.0005);
    glass1.position.setX(0);
    const glassBody = new Mesh();
    glassBody.geometry = createBoxOptions(
      sizeInfo.glassWidth,
      this.fixation == 2 ? sizeInfo.glassHeight + 0.2 : sizeInfo.glassHeight,
      sizeInfo.glassDepth,
      { top: false, bottom: false, left: false, right: false }
    );
    glassBody.material = glassMaterial;

    const glassSide = new Mesh();
    glassSide.geometry = createBoxOptions(
      sizeInfo.glassWidth,
      this.fixation == 2 ? sizeInfo.glassHeight + 0.2 : sizeInfo.glassHeight,
      sizeInfo.glassDepth,
      { front: false, back: false }
    );
    glassSide.material = glassSideMaterial;

    const glassInter = new Mesh();
    glassInter.geometry = new PlaneGeometry(
      sizeInfo.glassWidth,
      this.fixation == 2 ? sizeInfo.glassHeight + 0.2 : sizeInfo.glassHeight
    );
    glassInter.material = glassInterMaterial;

    glass1.add(glassSide);
    glass1.add(glassBody);
    glass1.add(glassInter);
    glassInter.renderOrder = -1;
    glass.add(glass1);

    const glass2 = glass1.clone();
    glass2.position.setY(0);
    glass2.position.setZ(-sizeInfo.glassDepth * 0.5 + -0.0005);
    glass2.position.setX(0);
    glass.add(glass2);

    const sPoint = new Point();
    sPoint.point = new Vector3(0, 0, 0);

    const ePoint = new Point();
    ePoint.point = new Vector3(sizeInfo.glassWidth, 0, 0);

    const mete = new Meter(undefined, sPoint, ePoint, {
      offset: new Vector3(0.0, 0.0, 0.0),
      rotation: new Euler(0, Math.PI, 0),
      scale: new Vector3(0.75, 1, 1),
      minScale: 0.75,
      maxScale: 0.75,
      debug: false,
    });
    mete.render();
    mete.root.position.setX(0);
    mete.root.position.setY(this.glassHeight * 0.5 + 0.05);
    if (this._isMeterVisible) glass.add(mete.root);
    glass.userData.meter = mete;

    return glass;
  }

  private _createBaseBox(sizeInfo: any) {
    const base = new Object3D();
    const baseMesh = new Mesh();
    baseMesh.geometry = new BoxGeometry(
      sizeInfo.baseWidth,
      sizeInfo.baseHeight,
      sizeInfo.baseDepth
    );

    baseMesh.material = baseMaterial;
    baseMesh.position.set(
      sizeInfo.baseWidth * 0.5,
      sizeInfo.baseHeight * 0.5,
      0
    );

    const kavcukLength = sizeInfo.baseWidth - 0.01 * 2;
    const kavcukMesh = new Mesh();
    kavcukMesh.material = kavcukMaterial;
    kavcukMesh.geometry = new BoxGeometry(kavcukLength, 0.127, 0.03);

    baseMesh.add(kavcukMesh);
    base.add(baseMesh);

    base.position.set(
      sizeInfo.basePaddingStart,
      sizeInfo.baseHeight * (this.fixation ? -1 : 0),
      sizeInfo.baseDepth * 0.5 + sizeInfo.paddingIn
    );

    return base;
  }

  private _createBase(sizeInfo: any) {
    if (this.fixation == 2) return new Mesh();
    //this._createBaseTopac(sizeInfo);
    return this._createBaseBox(sizeInfo);
  }

  execute(mesh?: Object3D) {
    if (!this.line) return;
    const sizeInfo = this._getSizeInfo();
    (this as any).glassWidth = sizeInfo.glassWidth;
    (this as any).glassCount = sizeInfo.glassCount;
    (this as any).baseWidth = sizeInfo.baseWidth;

    if (sizeInfo.hasGlass) {
      for (let i = 0; i < sizeInfo.glassCount; i++) {
        const glass = this._createGlass(sizeInfo, i);
        glass.position.setX(glass.position.x - this._line!.length * 0.5);
        this._glasses.push(glass);
        mesh?.add(glass);
      }
    }

    this._base = this._createBase(sizeInfo);
    this._base.position.setX(this._base.position.x - this._line!.length * 0.5);

    mesh?.add(this._base);
    this._executed = true;
  }

  dispose() {
    this._glasses.forEach((g) => {
      g?.removeFromParent();
      g?.traverse((gg) => {
        (gg as any)?.geometry?.dispose();
      });
      g?.userData.meter?.dispose();
    });

    //clear base
    this._base?.removeFromParent();
    this._base?.traverse((c) => {
      if (c instanceof Mesh) c.geometry?.dispose();
    });
    this._glasses = [];
  }
}
